// Hooks for the notification system.
// All notifications are created server-side by DB triggers or the
// check-loan-reminders cron edge function.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { captureSentryException } from '@/lib/sentry';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { mapSupabaseError } from '@/lib/errorMapper';
import { assertOnline } from '@/lib/networkGuard';
import { toast } from 'sonner';

export interface NotificationItem {
  id: string;
  user_id: string;
  type: string;
  payload: Record<string, string | undefined> | null;
  read_at: string | null;
  created_at: string;
}

const PAGE_SIZE = 10;

/**
 * Returns the count of unread notifications for the bell icon badge.
 *
 * Uses `head: true` to fetch only the count without returning row data.
 * Polls every 30 seconds and refetches on window focus as a fallback
 * for when Realtime is unavailable.
 *
 * @returns A React Query result whose `data` is the unread notification count.
 */
export function useNotificationCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notifications', 'count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      // `head: true` fetches only the count without returning row data.
      // [LEARN] `head: true` with `count: 'exact'` fetches only the row count without downloading any actual row data — efficient for badge numbers.
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
    // [LEARN] Polling: re-runs this query every 30 seconds to pick up new notifications as a fallback when Realtime WebSocket is unavailable.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetches paginated notifications for the current user via offset-based
 * infinite pagination (page size: 10).
 *
 * Overdue notifications are now generated server-side by the
 * check-loan-reminders cron edge function (runs every 6 hours).
 *
 * @returns A React Query infinite query result with `NotificationItem[]` pages.
 */
export function useNotifications() {
  const { user } = useAuth();

  // Offset-based infinite pagination: each page advances by PAGE_SIZE rows.
  return useInfiniteQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async ({ pageParam }): Promise<NotificationItem[]> => {
      if (!user) return [];

      const offset = typeof pageParam === 'number' ? pageParam : 0;
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) throw error;
      return (data ?? []) as NotificationItem[];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // No more pages when the last page returned fewer rows than the page size.
      // No more pages when fewer rows than pageSize
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    enabled: !!user,
    refetchOnWindowFocus: true,
  });
}

/**
 * Returns a mutation that marks a single notification as read.
 *
 * The `.is('read_at', null)` guard makes the update a no-op if the
 * notification is already read, avoiding unnecessary timestamp overwrites.
 * Invalidates both the notification list and count caches on success.
 *
 * @returns A React Query mutation accepting a notification ID string.
 */
export function useMarkNotificationRead() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await assertOnline();
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .is('read_at', null); // No-op if already read — avoids updating the timestamp again

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'count', user?.id] });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Returns a mutation that marks all unread notifications for the current
 * user as read in a single update.
 *
 * Invalidates both the notification list and count caches on success.
 *
 * @returns A React Query mutation (no arguments required).
 */
export function useMarkAllNotificationsRead() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user) return;
      await assertOnline();

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'count', user?.id] });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}
